import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  APP_MESSAGES,
  ApiResponseType,
  resolveApiFeedback
} from '../constants/app-messages.constants';

import {
  MessagePopupComponent,
  PopupType
} from '../component/message-popup/message-popup.component';

export interface PopupOptions {
  title: string;
  message: string;
  type?: PopupType;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  closable?: boolean;
  requireTextConfirmation?: boolean;
  expectedText?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  caseSensitive?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PopupService {
  constructor(private modalService: NgbModal) {}

  open(options: PopupOptions): Promise<boolean> {
    const modalRef = this.modalService.open(MessagePopupComponent, {
      centered: true,
      backdrop:
        options.showCancel || options.requireTextConfirmation
          ? 'static'
          : true,
      keyboard:
        !options.showCancel &&
        !options.requireTextConfirmation,
      windowClass: 'app-message-modal',
      backdropClass: 'app-message-backdrop',
      container: 'body'
    });

    const component =
      modalRef.componentInstance as MessagePopupComponent;

    component.title = options.title;
    component.message = options.message;
    component.type = options.type || 'info';

    component.confirmText =
      options.confirmText ||
      APP_MESSAGES.COMMON.BUTTONS.OK;

    component.cancelText =
      options.cancelText ||
      APP_MESSAGES.COMMON.BUTTONS.CANCEL;

    component.showCancel = Boolean(options.showCancel);
    component.closable = options.closable !== false;

    component.requireTextConfirmation =
      Boolean(options.requireTextConfirmation);

    component.expectedText = options.expectedText || '';
    component.inputLabel = options.inputLabel || '';
    component.inputPlaceholder = options.inputPlaceholder || '';
    component.caseSensitive = Boolean(options.caseSensitive);

    return modalRef.result.then(
      result => result === true,
      () => false
    );
  }

  success(
    message: string,
    title: string = APP_MESSAGES.COMMON.TITLES.SUCCESS
  ): Promise<boolean> {
    return this.open({
      title,
      message,
      type: 'success',
      confirmText: APP_MESSAGES.COMMON.BUTTONS.OK
    });
  }

  error(
    message: string,
    title: string = APP_MESSAGES.COMMON.TITLES.ERROR
  ): Promise<boolean> {
    return this.open({
      title,
      message,
      type: 'error',
      confirmText: APP_MESSAGES.COMMON.BUTTONS.CLOSE
    });
  }

  warning(
    message: string,
    title: string = APP_MESSAGES.COMMON.TITLES.WARNING
  ): Promise<boolean> {
    return this.open({
      title,
      message,
      type: 'warning',
      confirmText: APP_MESSAGES.COMMON.BUTTONS.OK
    });
  }

  info(
    message: string,
    title: string = APP_MESSAGES.COMMON.TITLES.INFORMATION
  ): Promise<boolean> {
    return this.open({
      title,
      message,
      type: 'info',
      confirmText: APP_MESSAGES.COMMON.BUTTONS.OK
    });
  }

  validation(
    message: string,
    title: string = APP_MESSAGES.COMMON.TITLES.VALIDATION
  ): Promise<boolean> {
    return this.warning(message, title);
  }

  confirm(
    message: string,
    title: string = APP_MESSAGES.COMMON.TITLES.CONFIRMATION,
    confirmText: string = APP_MESSAGES.COMMON.BUTTONS.CONFIRM,
    cancelText: string = APP_MESSAGES.COMMON.BUTTONS.CANCEL,
    type: PopupType = 'warning'
  ): Promise<boolean> {
    return this.open({
      title,
      message,
      type,
      confirmText,
      cancelText,
      showCancel: true,
      closable: false
    });
  }

  confirmWithText(
    message: string,
    title: string,
    expectedText: string,
    confirmText: string = APP_MESSAGES.COMMON.BUTTONS.YES_CONFIRM,
    cancelText: string = APP_MESSAGES.COMMON.BUTTONS.CANCEL,
    type: PopupType = 'error'
  ): Promise<boolean> {
    return this.open({
      title,
      message,
      type,
      confirmText,
      cancelText,
      showCancel: true,
      closable: false,
      requireTextConfirmation: true,
      expectedText,
      inputLabel: 'Confirmation required',
      inputPlaceholder: `Type ${expectedText}`,
      caseSensitive: false
    });
  }

  apiError(
    error: any,
    fallback: string,
    title: string = APP_MESSAGES.COMMON.TITLES.ERROR
  ): Promise<boolean> {
    return this.showApiFeedback(
      error,
      fallback,
      title,
      'ERROR'
    );
  }

  apiSuccess(
    response: any,
    fallback: string,
    title: string = APP_MESSAGES.COMMON.TITLES.SUCCESS
  ): Promise<boolean> {
    return this.showApiFeedback(
      response,
      fallback,
      title,
      'SUCCESS'
    );
  }

  apiFeedback(
    source: any,
    fallback: string,
    title: string,
    fallbackType: ApiResponseType
  ): Promise<boolean> {
    return this.showApiFeedback(
      source,
      fallback,
      title,
      fallbackType
    );
  }

  apiConfirm(
    source: any,
    fallback: string,
    title: string,
    confirmText: string = APP_MESSAGES.COMMON.BUTTONS.RETRY,
    cancelText: string = APP_MESSAGES.COMMON.BUTTONS.CLOSE,
    fallbackType: ApiResponseType = 'ERROR'
  ): Promise<boolean> {
    const feedback = resolveApiFeedback(
      source,
      { title, message: fallback },
      fallbackType
    );

    return this.confirm(
      feedback.message,
      feedback.title,
      confirmText,
      cancelText,
      this.toPopupType(feedback.type)
    );
  }

  private showApiFeedback(
    source: any,
    fallback: string,
    title: string,
    fallbackType: ApiResponseType
  ): Promise<boolean> {
    const feedback = resolveApiFeedback(
      source,
      { title, message: fallback },
      fallbackType
    );

    return this.open({
      title: feedback.title,
      message: feedback.message,
      type: this.toPopupType(feedback.type),
      confirmText:
        feedback.type === 'ERROR'
          ? APP_MESSAGES.COMMON.BUTTONS.CLOSE
          : APP_MESSAGES.COMMON.BUTTONS.OK
    });
  }

  private toPopupType(type: ApiResponseType): PopupType {
    switch (type) {
      case 'SUCCESS':
        return 'success';

      case 'ERROR':
        return 'error';

      case 'WARNING':
      case 'VALIDATION':
      case 'CONFIRMATION':
        return 'warning';

      case 'INFO':
      default:
        return 'info';
    }
  }
}